/**
 * signupAttribution.ts — capture UTMs + referrer + landing page once per
 * browser session (Admin Visibility Phase 1.2).
 *
 * Called from index.tsx on every page load. Stored in sessionStorage so it
 * survives SPA navigation but clears on tab close — we only care about the
 * session that ultimately converts.
 */

import { safeSession } from '@/utils/safeStorage';
const STORAGE_KEY = 'ts_signup_attribution';

export interface SignupAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
}

export function captureAttributionOnce(): void {
  if (typeof window === 'undefined') return;
  if (safeSession.get(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const attribution: Record<string, string> = {};
  const keys: Array<keyof SignupAttribution> = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  ];
  keys.forEach(k => {
    const v = params.get(k);
    if (v) attribution[k] = v;
  });
  if (document.referrer) attribution.referrer = document.referrer;
  attribution.landing_page = window.location.pathname + window.location.search;

  if (Object.keys(attribution).length > 0) {
    safeSession.set(STORAGE_KEY, JSON.stringify(attribution));
  }
}

export function getAttribution(): SignupAttribution | null {
  if (typeof window === 'undefined') return null;
  const raw = safeSession.get(STORAGE_KEY);
  return raw ? JSON.parse(raw) : null;
}

/**
 * Build the payload for /auth/register signup_source, combining captured
 * attribution with the current metro + session id.
 */
export function buildSignupSource(
  selectedMetro: string | null,
  sessionId: string | null,
): Record<string, unknown> {
  const attribution = getAttribution() || {};
  return {
    ...attribution,
    ...(selectedMetro ? { first_metro: selectedMetro } : {}),
    ...(sessionId ? { first_session_id: sessionId } : {}),
  };
}
